import { createWithEqualityFn } from 'zustand/traditional';
import { persist, devtools } from 'zustand/middleware';
import dayjs from 'dayjs';
import { userSettingsApi } from '@features/settings/api/userSettingsApi';

// ✅ ОПТИМИЗАЦИЯ: Worker для тяжелых операций очистки
let cleanupWorker: Worker | null = null;
if (typeof window !== 'undefined' && window.Worker) {
  cleanupWorker = new Worker(new URL('./historyCleanupWorker.ts', import.meta.url), { type: 'module' });
  
  // ✅ Обработка ответа от Worker (исправление race condition)
  cleanupWorker.onmessage = (e) => {
    if (e.data?.purgedDeviceIds) {
      const { purgedDeviceIds } = e.data;
      
      // Выполняем удаление в основном потоке, сохраняя актуальные данные других устройств
      useDeviceInterfaceHistoryStore.setState((state) => {
        const nextHistory = { ...state.history };
        const nextUpdates = { ...state.lastUpdates };
        const nextAccess = { ...state.lastDeviceAccess };
        
        purgedDeviceIds.forEach((id: number) => {
          Object.keys(nextHistory).forEach(key => {
            if (key.startsWith(`${id}:`)) delete nextHistory[key];
          });
          delete nextUpdates[id];
          delete nextAccess[id];
        });
        
        return { history: nextHistory, lastUpdates: nextUpdates, lastDeviceAccess: nextAccess };
      });
    }
  };
}

interface RawInterfaceSample {
  index: number;
  name?: string;
  in_octets?: number;
  out_octets?: number;
  in_errors?: number;
  out_errors?: number;
  errors_measured?: boolean;
}

export type InterfaceHistorySource = 'cache' | 'sync' | 'api' | 'realtime';

export interface InterfaceHistoryPoint {
  timestamp: string;
  source: InterfaceHistorySource;
  inOctets: number;
  outOctets: number;
  inErrors: number;
  outErrors: number;
  // Absent in older/legacy points means historical behavior; false is explicit unknown.
  errorsMeasured?: boolean;
}

export type InterfaceUpdateMeta = Partial<Record<InterfaceHistorySource, string>>;

interface InterfaceRealtimeChange {
  indexes: number[];
  timestamp: string;
  source: InterfaceHistorySource;
}

interface InterfaceHistoryState {
  history: Record<string, InterfaceHistoryPoint[]>;
  lastUpdates: Record<number, InterfaceUpdateMeta>;
  lastDeviceAccess: Record<number, number>; // Время последнего доступа к устройству (timestamp)
  pinnedInterfaces: Record<number, number[]>; // deviceId -> array of ifIndexes
  pinnedVersion: number; // Версия для синхронизации
  silencedInterfaces: Record<string, { failureCount: number; lastFailureReason: string; timestamp: string }>; // "deviceId:ifIndex" -> error info
  realtimeChanges: Record<number, InterfaceRealtimeChange | undefined>;
  addSnapshot: (
    deviceId: number,
    generatedAt: string,
    samples: RawInterfaceSample[],
    source: InterfaceHistorySource,
  ) => void;
  clear: () => void;
  /**
   * Сбросить ТОЛЬКО пересчитываемое: историю трафика, отметки последнего обновления и
   * realtime-события. Пометки оператора — закреплённые и заглушённые порты — остаются.
   *
   * ⚠️ Отличие от `clear()` принципиальное, а не косметическое: `partialize` этого стора
   * сохраняет РОВНО `pinnedInterfaces` и `silencedInterfaces`, то есть именно их продукт
   * объявляет долговечными и принадлежащими оператору. `clear()` уместен при выходе из
   * системы; на смене устройства он стирал бы то, что оператор пометил руками.
   */
  clearVolatile: () => void;
  getHistory: (deviceId: number, ifIndex: number) => InterfaceHistoryPoint[];
  getLastUpdates: (deviceId: number) => InterfaceUpdateMeta;
  registerRealtimeChange: (deviceId: number, timestamp: string, indexes: number[], source?: InterfaceHistorySource) => void;
  updateLastUpdate: (deviceId: number, timestamp: string, source: InterfaceHistorySource) => void;
  consumeRealtimeChange: (deviceId: number) => void;
  getRealtimeChange: (deviceId: number) => InterfaceRealtimeChange | null;
  touchDevice: (deviceId: number) => void; // Обновить время доступа к устройству
  togglePin: (deviceId: number, ifIndex: number) => void; // Закрепить/открепить интерфейс
  pinAllActive: (deviceId: number, activeIfIndexes: number[]) => void; // Закрепить все активные порты
  resetSilenced: (deviceId: number) => void; // Сбросить все заглушенные порты устройства
  setPinnedInterfaces: (pinned: Record<number, number[]>, version?: number) => void; // Установить все пины (из бэкенда)
  isPinned: (deviceId: number, ifIndex: number) => boolean;
  isSilenced: (deviceId: number, ifIndex: number) => boolean;
  markFailure: (deviceId: number, ifIndex: number, reason: string) => void;
  clearFailures: (deviceId: number, ifIndex: number) => void;
}

export const MAX_POINTS_PER_INTERFACE = 32;
export const MAX_HISTORY_KEYS = 2000; // Ограничиваем количество ключей истории
const DEVICE_CACHE_TTL_MS = 10 * 60 * 1000; // 10 минут неактивности устройства перед очисткой истории
const FAILURE_THRESHOLD = 3; // Порог ошибок для Circuit Breaker
const SILENCE_AUTO_RESET_MS = 60 * 60 * 1000; // 1 час до автоматического сброса блокировки

// ✅ БЕЗОПАСНОСТЬ: Валидация сэмплов трафика
const validateSample = (sample: RawInterfaceSample, deviceId: number, state: InterfaceHistoryState): { valid: boolean; reason?: string } => {
  const inOctets = toNumber(sample.in_octets);
  const outOctets = toNumber(sample.out_octets);
  
  // 1. Не допускаем отрицательные значения
  if (inOctets < 0 || outOctets < 0) return { valid: false, reason: 'Negative octets' };
  
  // 2. Защита от аномальных всплесков (более 1 Петабита за один замер)
  // Это может быть вызвано ошибкой в прошивке или SNMP-ответе
  const MAX_CAPACITY = 1_000_000_000_000_000 / 8; // 1 Pbps in octets
  if (inOctets > MAX_CAPACITY || outOctets > MAX_CAPACITY) return { valid: false, reason: 'Abnormal spike' };
  
  return { valid: true };
};

const getKey = (deviceId: number, ifIndex: number) => `${deviceId}:${ifIndex}`;

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (typeof value === 'bigint') {
    return Number(value);
  }
  return 0;
};

export const useDeviceInterfaceHistoryStore = createWithEqualityFn<InterfaceHistoryState>()(
  persist(
    (set, get) => ({
      history: {},
      lastUpdates: {},
      lastDeviceAccess: {},
      pinnedInterfaces: {},
      pinnedVersion: 0,
      silencedInterfaces: {},
      realtimeChanges: {},
      addSnapshot: (deviceId, generatedAt, samples, source) => {
        if (!generatedAt || samples.length === 0) return;

        // ✅ БЕЗОПАСНОСТЬ: Валидация сэмплов с учетом Circuit Breaker
        const state = get();
        const validSamples: RawInterfaceSample[] = [];

        samples.forEach((sample) => {
          const key = getKey(deviceId, sample.index);
          const silenceInfo = state.silencedInterfaces[key];

          // ✅ БЕЗОПАСНОСТЬ: Автоматический сброс Circuit Breaker через 1 час
          if (silenceInfo && silenceInfo.failureCount >= FAILURE_THRESHOLD) {
            const lastFailure = dayjs(silenceInfo.timestamp);
            if (dayjs().diff(lastFailure) > SILENCE_AUTO_RESET_MS) {
              state.clearFailures(deviceId, sample.index);
            } else {
              return; // Интерфейс всё еще "заглушен"
            }
          }

          const validation = validateSample(sample, deviceId, state);
          if (validation.valid) {
            validSamples.push(sample);
            // Если сэмпл валиден, можно было бы сбрасывать счетчик, но лучше делать это явно
          } else {
            // Маркируем ошибку
            state.markFailure(deviceId, sample.index, validation.reason || 'Unknown');
          }
        });

        if (validSamples.length === 0) return;

        // Проверяем, не перезаписываем ли мы ненулевые данные нулями
        const allZero = validSamples.every((s) => {
          const inOctets = toNumber(s.in_octets);
          const outOctets = toNumber(s.out_octets);
          return inOctets === 0 && outOctets === 0;
        });
        
        if (allZero) {
          const currentTimestamp = dayjs(generatedAt).isValid() ? dayjs(generatedAt) : dayjs();
          const now = dayjs();
          let validatedGeneratedAt = generatedAt;
          if (currentTimestamp.isAfter(now.add(5, 'minute')) || currentTimestamp.isBefore(now.subtract(24, 'hour'))) {
            validatedGeneratedAt = now.toISOString();
          }

          const shouldSkip = validSamples.some((sample) => {
            const key = getKey(deviceId, sample.index);
            const history = get().history[key] ?? [];
            if (history.length === 0) return false;
            const hasNonZero = history.some((p) => p.inOctets > 0 || p.outOctets > 0);
            if (!hasNonZero) return false;
            const lastPoint = history[history.length - 1];
            const lastTimestamp = dayjs(lastPoint.timestamp);
            return lastTimestamp.isAfter(dayjs(validatedGeneratedAt)) || lastTimestamp.isSame(dayjs(validatedGeneratedAt));
          });
          
          if (shouldSkip) return;
        }

        const currentTimestamp = dayjs(generatedAt);
        const now = dayjs();
        let timestamp = currentTimestamp.isValid() ? currentTimestamp.toISOString() : now.toISOString();
        
        if (currentTimestamp.isValid() && (currentTimestamp.isAfter(now.add(5, 'minute')) || currentTimestamp.isBefore(now.subtract(24, 'hour')))) {
          timestamp = now.toISOString();
        }

        set((state) => {
          const nextHistory = { ...state.history };
          const nextUpdates = { ...state.lastUpdates };
          const nextAccess = { ...state.lastDeviceAccess };

          nextAccess[deviceId] = Date.now();

          validSamples.forEach((sample) => {
            const ifIndex = sample.index;
            if (ifIndex == null) return;
            const key = getKey(deviceId, ifIndex);
            const prev = nextHistory[key] ?? [];
            const point: InterfaceHistoryPoint = {
              timestamp,
              source,
              inOctets: toNumber(sample.in_octets),
              outOctets: toNumber(sample.out_octets),
              inErrors: toNumber(sample.in_errors),
              outErrors: toNumber(sample.out_errors),
              errorsMeasured: sample.errors_measured !== false,
            };
            const updated = [...prev, point];
            if (updated.length > MAX_POINTS_PER_INTERFACE) {
              updated.splice(0, updated.length - MAX_POINTS_PER_INTERFACE);
            }
            nextHistory[key] = updated;
          });

          const deviceMeta = { ...(nextUpdates[deviceId] ?? {}) };
          deviceMeta[source] = timestamp;
          nextUpdates[deviceId] = deviceMeta;

          const currentKeysCount = Object.keys(nextHistory).length;
          if (cleanupWorker && (currentKeysCount > MAX_HISTORY_KEYS * 0.9)) {
            cleanupWorker.postMessage({
              history: nextHistory,
              lastDeviceAccess: nextAccess,
              deviceId,
              maxHistoryKeys: MAX_HISTORY_KEYS,
              deviceCacheTtlMs: DEVICE_CACHE_TTL_MS
            });
          }

          return { 
            history: nextHistory, 
            lastUpdates: nextUpdates, 
            lastDeviceAccess: nextAccess 
          };
        });
      },
      clear: () => set({ 
        history: {}, 
        lastUpdates: {}, 
        lastDeviceAccess: {}, 
        pinnedInterfaces: {}, 
        pinnedVersion: 0,
        silencedInterfaces: {}, 
        realtimeChanges: {} 
      }),
      clearVolatile: () => set({
        history: {},
        lastUpdates: {},
        lastDeviceAccess: {},
        realtimeChanges: {},
      }),
      getHistory: (deviceId, ifIndex) => {
        const key = getKey(deviceId, ifIndex);
        return get().history[key] ?? [];
      },
      getLastUpdates: (deviceId) => get().lastUpdates[deviceId] ?? {},
      registerRealtimeChange: (deviceId, timestamp, indexes, source = 'realtime') => {
        if (!Number.isFinite(deviceId) || indexes.length === 0) return;
        const deduped = Array.from(new Set(indexes.filter((index) => Number.isFinite(index))));
        if (deduped.length === 0) return;
        const normalizedTimestamp = dayjs(timestamp).isValid() ? dayjs(timestamp).toISOString() : dayjs().toISOString();

        set((state) => {
          const nextUpdates = { ...state.lastUpdates };
          const nextAccess = { ...state.lastDeviceAccess };
          nextAccess[deviceId] = Date.now();
          
          const deviceMeta = { ...(nextUpdates[deviceId] ?? {}) };
          deviceMeta[source] = normalizedTimestamp;
          nextUpdates[deviceId] = deviceMeta;

          const nextRealtime = { ...state.realtimeChanges };
          nextRealtime[deviceId] = {
            indexes: deduped,
            timestamp: normalizedTimestamp,
            source,
          };

          return {
            lastUpdates: nextUpdates,
            realtimeChanges: nextRealtime,
            lastDeviceAccess: nextAccess
          };
        });
      },
      consumeRealtimeChange: (deviceId) => {
        set((state) => {
          if (!(deviceId in state.realtimeChanges)) return state;
          const nextRealtime = { ...state.realtimeChanges };
          delete nextRealtime[deviceId];
          return { realtimeChanges: nextRealtime };
        });
      },
      getRealtimeChange: (deviceId) => get().realtimeChanges[deviceId] ?? null,
      updateLastUpdate: (deviceId, timestamp, source) => {
        if (!Number.isFinite(deviceId)) return;
        const normalizedTimestamp = dayjs(timestamp).isValid() ? dayjs(timestamp).toISOString() : dayjs().toISOString();

        set((state) => {
          const nextUpdates = { ...state.lastUpdates };
          const nextAccess = { ...state.lastDeviceAccess };
          nextAccess[deviceId] = Date.now();

          const deviceMeta = { ...(nextUpdates[deviceId] ?? {}) };
          deviceMeta[source] = normalizedTimestamp;
          nextUpdates[deviceId] = deviceMeta;

          return { 
            lastUpdates: nextUpdates,
            lastDeviceAccess: nextAccess
          };
        });
      },
      touchDevice: (deviceId) => {
        set((state) => {
          const nextAccess = { ...state.lastDeviceAccess };
          nextAccess[deviceId] = Date.now();
          return { lastDeviceAccess: nextAccess };
        });
      },
      togglePin: (deviceId, ifIndex) => {
        set((state) => {
          const pins = { ...state.pinnedInterfaces };
          const devicePins = [...(pins[deviceId] || [])];
          const index = devicePins.indexOf(ifIndex);
          const nextVersion = state.pinnedVersion + 1;
          
          if (index > -1) {
            devicePins.splice(index, 1);
          } else {
            devicePins.push(ifIndex);
          }
          
          if (devicePins.length === 0) {
            delete pins[deviceId];
          } else {
            pins[deviceId] = devicePins;
          }
          
          // ✅ АРХИТЕКТУРА: Синхронизация с бэкендом (версионность для разрешения конфликтов)
          const devicePrefs: Record<string, number[]> = {};
          Object.entries(pins).forEach(([id, indexes]) => {
            devicePrefs[id] = indexes;
          });
          
          userSettingsApi.updateSettings({
            device_preferences: {
              version: nextVersion,
              pinned_interfaces: devicePrefs
            }
          }).catch(err => console.error('[HistoryStore] Failed to sync pins with backend:', err));
          
          return { pinnedInterfaces: pins, pinnedVersion: nextVersion };
        });
      },
      setPinnedInterfaces: (pinned, version) => {
        set((state) => {
          // Если пришла версия старее текущей локальной - игнорируем (разрешение конфликтов)
          if (version !== undefined && version < state.pinnedVersion) {
            console.warn(`[HistoryStore] Received stale pinnedInterfaces version (${version} < ${state.pinnedVersion}), ignoring.`);
            return state;
          }
          return { pinnedInterfaces: pinned, pinnedVersion: version ?? state.pinnedVersion };
        });
      },
      pinAllActive: (deviceId, activeIfIndexes) => {
        set((state) => {
          const pins = { ...state.pinnedInterfaces };
          const devicePins = new Set(pins[deviceId] || []);
          const nextVersion = state.pinnedVersion + 1;
          activeIfIndexes.forEach(idx => devicePins.add(idx));
          
          pins[deviceId] = Array.from(devicePins);
          
          // Синхронизация с бэкендом
          const devicePrefs: Record<string, number[]> = {};
          Object.entries(pins).forEach(([id, indexes]) => {
            devicePrefs[id] = indexes;
          });
          
          userSettingsApi.updateSettings({
            device_preferences: {
              version: nextVersion,
              pinned_interfaces: devicePrefs
            }
          }).catch(err => console.error('[HistoryStore] Failed to sync pins with backend:', err));
          
          return { pinnedInterfaces: pins, pinnedVersion: nextVersion };
        });
      },
      resetSilenced: (deviceId) => {
        set((state) => {
          const nextSilenced = { ...state.silencedInterfaces };
          Object.keys(nextSilenced).forEach(key => {
            if (key.startsWith(`${deviceId}:`)) {
              delete nextSilenced[key];
            }
          });
          return { silencedInterfaces: nextSilenced };
        });
      },
      isPinned: (deviceId, ifIndex) => {
        return (get().pinnedInterfaces[deviceId] || []).includes(ifIndex);
      },
      isSilenced: (deviceId, ifIndex) => {
        const key = getKey(deviceId, ifIndex);
        const info = get().silencedInterfaces[key];
        return info ? info.failureCount >= FAILURE_THRESHOLD : false;
      },
      markFailure: (deviceId, ifIndex, reason) => {
        set((state) => {
          const key = getKey(deviceId, ifIndex);
          const current = state.silencedInterfaces[key] || { failureCount: 0, lastFailureReason: '', timestamp: '' };
          
          const nextSilenced = { ...state.silencedInterfaces };
          const failureCount = current.failureCount + 1;
          nextSilenced[key] = {
            failureCount,
            lastFailureReason: reason,
            timestamp: new Date().toISOString()
          };
          
          // ✅ БЕЗОПАСНОСТЬ: Логирование в системный журнал (console.error для диагностики)
          if (failureCount >= FAILURE_THRESHOLD) {
            console.error(`[CircuitBreaker] 🛡️ Interface SILENCED: deviceId=${deviceId}, ifIndex=${ifIndex}, reason="${reason}", totalFailures=${failureCount}`);
          } else {
            console.warn(`[CircuitBreaker] ⚠️ Interface failure: deviceId=${deviceId}, ifIndex=${ifIndex}, reason="${reason}", attempt=${failureCount}/${FAILURE_THRESHOLD}`);
          }
          
          return { silencedInterfaces: nextSilenced };
        });
      },
      clearFailures: (deviceId, ifIndex) => {
        set((state) => {
          const key = getKey(deviceId, ifIndex);
          if (!state.silencedInterfaces[key]) return state;
          
          const nextSilenced = { ...state.silencedInterfaces };
          delete nextSilenced[key];
          
          console.info(`[CircuitBreaker] ✅ Interface failures cleared: deviceId=${deviceId}, ifIndex=${ifIndex}`);
          return { silencedInterfaces: nextSilenced };
        });
      },
    }),
    {
      name: 'workernet-device-history-storage',
      partialize: (state) => ({
        pinnedInterfaces: state.pinnedInterfaces,
        silencedInterfaces: state.silencedInterfaces,
      }),
    }
  )
);
